import React, { createContext, useContext, useState, useEffect } from 'react';
import { publicSettingsAPI } from '../services/api';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Currency configuration
const CURRENCY_CONFIG = {
  INR: {
    symbol: '₹',
    code: 'INR',
    name: 'Indian Rupee',
    locale: 'en-IN',
    displayName: 'Rs',
    position: 'before', // symbol position relative to amount
    decimalPlaces: 2
  },
  USD: {
    symbol: '$',
    code: 'USD',
    name: 'US Dollar',
    locale: 'en-US',
    displayName: 'USD',
    position: 'before',
    decimalPlaces: 2
  },
  EUR: {
    symbol: '€',
    code: 'EUR',
    name: 'Euro',
    locale: 'en-EU',
    displayName: 'EUR',
    position: 'before',
    decimalPlaces: 2
  },
  GBP: {
    symbol: '£',
    code: 'GBP',
    name: 'British Pound',
    locale: 'en-GB',
    displayName: 'GBP',
    position: 'before',
    decimalPlaces: 2
  }
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('INR');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCurrencySettings = async () => {
    try {
      setLoading(true);
      const response = await publicSettingsAPI.getSettings();
      
      if (response.data && response.data.data && response.data.data.settings) {
        const backendSettings = response.data.data.settings;
        const currencySetting = backendSettings.currency?.value || 'INR';
        setCurrency(currencySetting);
      }
    } catch (err) {
      console.error('Error fetching currency settings:', err);
      setError(err.message);
      // Keep default currency on error
    } finally {
      setLoading(false);
    }
  };

  // Format price with current currency
  const formatPrice = (amount, options = {}) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      amount = 0;
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) {
      return `${CURRENCY_CONFIG[currency].symbol}0.00`;
    }

    const config = CURRENCY_CONFIG[currency];
    const formatOptions = {
      style: 'currency',
      currency: config.code,
      minimumFractionDigits: options.decimalPlaces || config.decimalPlaces,
      maximumFractionDigits: options.decimalPlaces || config.decimalPlaces,
      ...options
    };

    return new Intl.NumberFormat(config.locale, formatOptions).format(numericAmount);
  };

  // Format price with custom currency
  const formatPriceWithCurrency = (amount, currencyCode, options = {}) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      amount = 0;
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) {
      return `${CURRENCY_CONFIG[currencyCode]?.symbol || '$'}0.00`;
    }

    const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.INR;
    const formatOptions = {
      style: 'currency',
      currency: config.code,
      minimumFractionDigits: options.decimalPlaces || config.decimalPlaces,
      maximumFractionDigits: options.decimalPlaces || config.decimalPlaces,
      ...options
    };

    return new Intl.NumberFormat(config.locale, formatOptions).format(numericAmount);
  };

  // Get currency symbol
  const getCurrencySymbol = (currencyCode = currency) => {
    return CURRENCY_CONFIG[currencyCode]?.symbol || '₹';
  };

  // Get currency display name
  const getCurrencyDisplayName = (currencyCode = currency) => {
    return CURRENCY_CONFIG[currencyCode]?.displayName || 'Rs';
  };

  // Get currency name
  const getCurrencyName = (currencyCode = currency) => {
    return CURRENCY_CONFIG[currencyCode]?.name || 'Indian Rupee';
  };

  // Get current currency config
  const getCurrencyConfig = (currencyCode = currency) => {
    return CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.INR;
  };

  // Update currency
  const updateCurrency = async (newCurrency) => {
    try {
      // In a real implementation, you would call the admin API to update the setting
      setCurrency(newCurrency);
      return true;
    } catch (err) {
      console.error('Error updating currency:', err);
      return false;
    }
  };

  // Get all available currencies
  const getAvailableCurrencies = () => {
    return Object.entries(CURRENCY_CONFIG).map(([code, config]) => ({
      code,
      symbol: config.symbol,
      name: config.name,
      displayName: config.displayName
    }));
  };

  useEffect(() => {
    fetchCurrencySettings();
  }, []);

  const value = {
    currency,
    loading,
    error,
    formatPrice,
    formatPriceWithCurrency,
    getCurrencySymbol,
    getCurrencyDisplayName,
    getCurrencyName,
    getCurrencyConfig,
    updateCurrency,
    getAvailableCurrencies,
    fetchCurrencySettings
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}; 